/**
 * Matchmaking logic for One Group to Rule Them All.
 *
 * Teams enter a queue by publishing ephemeral KIND_MATCHMAKING_QUEUE events.
 * Matchmaking searches for compatible opponents based on:
 * - Same game type
 * - Compatible rating bracket (widens over time)
 * - Compatible team size
 */

import type { TeamRating } from "./ratings";
import {
  KIND_MATCHMAKING_QUEUE,
  KIND_MATCH_FOUND,
  KIND_READY,
  TAG_GAME_TYPE,
  TAG_TEAM_A,
  TAG_TEAM_B,
  TAG_GAME_ID,
  TAG_READY,
} from "./gameKinds";

/** A team's entry in the matchmaking queue */
export interface QueueEntry {
  teamId: string;
  teamName: string;
  gameType: string;
  rating: number;
  memberCount: number;
  memberPubkeys: string[];
  enteredAt: number; // Unix timestamp
}

/** A matched pair of teams */
export interface MatchPair {
  gameId: string;   // Unique game ID (hex)
  teamA: QueueEntry;
  teamB: QueueEntry;
  matchedAt: number;
}

/**
 * Calculate a compatibility score between two queue entries.
 * Higher = better match. Returns -1 if incompatible.
 */
export function compatibilityScore(a: QueueEntry, b: QueueEntry): number {
  // Must be same game type
  if (a.gameType !== b.gameType) return -1;

  // Cannot match against self
  if (a.teamId === b.teamId) return -1;

  // Rating difference
  const ratingDiff = Math.abs(a.rating - b.rating);
  // Time waiting in queue (seconds)
  const waitTimeA = Math.max(0, (Date.now() / 1000) - a.enteredAt);
  const waitTimeB = Math.max(0, (Date.now() / 1000) - b.enteredAt);
  const maxWait = Math.max(waitTimeA, waitTimeB);

  // Rating tolerance widens with wait time (50 points per 10 seconds, max 500)
  const tolerance = Math.min(500, 50 + maxWait * 5);

  if (ratingDiff > tolerance) return -1;

  // Score: lower rating diff and higher wait = better match
  const ratingScore = 1 - (ratingDiff / (tolerance + 1));
  const waitScore = Math.min(1, maxWait / 60); // Caps at 60s
  return ratingScore * 0.7 + waitScore * 0.3;
}

/** Find the best match from a pool of queue entries */
export function findBestMatch(
  entry: QueueEntry,
  pool: QueueEntry[],
): QueueEntry | null {
  let best: QueueEntry | null = null;
  let bestScore = -1;

  for (const candidate of pool) {
    const score = compatibilityScore(entry, candidate);
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }

  return best;
}

/** Generate a unique game ID from two team IDs */
export function generateGameId(teamA: string, teamB: string, timestamp: number): string {
  // Simple deterministic game ID: hash of sorted team IDs + timestamp
  const [first, second] = [teamA, teamB].sort();
  const input = `${first}:${second}:${timestamp}`;
  // Use a simple hash (in production, use proper crypto)
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit
  }
  return Math.abs(hash).toString(16).padStart(16, "0");
}

/** Build a matchmaking queue Nostr event */
export function buildQueueEvent(entry: QueueEntry) {
  return {
    kind: KIND_MATCHMAKING_QUEUE,
    content: "",
    tags: [
      [TAG_GAME_TYPE, entry.gameType],
      ["team_id", entry.teamId],
      ["team_name", entry.teamName],
      ["rating", String(entry.rating)],
      ["members", String(entry.memberCount)],
      ...entry.memberPubkeys.map((pk) => ["p", pk]),
    ],
  };
}

/** Build a match found Nostr event */
export function buildMatchFoundEvent(match: MatchPair) {
  return {
    kind: KIND_MATCH_FOUND,
    content: JSON.stringify({
      gameId: match.gameId,
      gameType: match.teamA.gameType,
    }),
    tags: [
      [TAG_GAME_ID, match.gameId],
      [TAG_GAME_TYPE, match.teamA.gameType],
      [TAG_TEAM_A, match.teamA.teamId],
      [TAG_TEAM_B, match.teamB.teamId],
      ["team_a_name", match.teamA.teamName],
      ["team_b_name", match.teamB.teamName],
    ],
  };
}

/** Build a ready-state Nostr event */
export function buildReadyEvent(
  teamId: string,
  gameType: string,
  memberPubkey: string,
  isReady: boolean,
) {
  return {
    kind: KIND_READY,
    content: "",
    tags: [
      [TAG_GAME_TYPE, gameType],
      ["team_id", teamId],
      ["p", memberPubkey],
      [TAG_READY, isReady ? "true" : "false"],
    ],
  };
}